import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, Pencil, Plus, Scissors, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rp } from "@/lib/payroll";
import { bankColor, rekParts } from "@/lib/bank";
import { TID } from "@/constants/testIds";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useIsMobile } from "@/lib/useIsMobile";
import { useT } from "@/i18n";

const Pill = ({ tone = "gray", children }) => {
  const tones = {
    navy: "bg-[#EEF3FA] text-[#0071E3]",
    red: "bg-[#FDF0F2] text-[#C8102E]",
    gray: "bg-[#F2F2F7] text-[#6E6E73]",
  };
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[12px] font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

export default function Employees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [delTarget, setDelTarget] = useState(null);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.role === "viewer";
  const readOnly = user?.role === "viewer";
  const { t } = useT();

  const [access, setAccess] = useState(null);

  const loadAccess = () =>
    api.get("/employee-access").then((r) => setAccess(r.data)).catch(() => {});

  useEffect(() => {
    loadAccess();
  }, []);

  const canEdit = isAdmin || access?.state === "approved";
  const requestPermission = async () => {
    try {
      await api.post("/employee-access/request");
      toast.success("Permintaan izin dikirim ke admin");
      loadAccess();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const load = async () => {
    try {
      const { data } = await api.get("/employees");
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(
      (e) =>
        e.nama.toLowerCase().includes(s) ||
        (e.bank || "").toLowerCase().includes(s) ||
        (e.jabatan || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const startAdd = () => {
    if (!canEdit) { toast.error("Minta izin ke admin dulu"); return; }
    navigate("/employees/new");
  };

  const startEdit = (emp) => {
    if (!canEdit) { toast.error("Minta izin ke admin dulu"); return; }
    navigate(`/employees/${emp.id}?edit=1`);
  };

  const remove = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/employees/${delTarget.id}`);
      setItems((l) => l.filter((x) => x.id !== delTarget.id));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDelTarget(null);
    }
  };

  return (
    <div data-testid={TID.employees.root} className="space-y-6">
      {!isAdmin && access && access.state !== "approved" && (
        <div
          data-testid="emp-access-banner"
          className="flex flex-col gap-3.5 rounded-[20px] border border-white/60 bg-white/60 p-4 shadow-[0_10px_40px_rgba(0,0,0,0.08)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#0071E3]/10 text-[#0071E3]">
              <Lock className="h-[18px] w-[18px]" />
            </span>
            <p className="text-[13.5px] leading-snug text-[#3A3A3C]">
              {access.state === "pending"
                ? t("emp.accessPending")
                : t("emp.accessNeeded")}
            </p>
          </div>
          {access.state !== "pending" && (
            <button
              data-testid="emp-access-request"
              onClick={requestPermission}
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-[#0071E3] px-5 text-[14px] font-semibold text-white shadow-[0_8px_20px_rgba(0,113,227,0.35)] transition-all hover:bg-[#0077ED] hover:shadow-[0_10px_26px_rgba(0,113,227,0.45)] active:scale-[0.97]"
            >
              <Lock className="h-4 w-4" />
              {t("emp.requestPermission")}
            </button>
          )}
        </div>
      )}
      {!isAdmin && access?.state === "approved" && (
        <p data-testid="emp-access-granted" className="rounded-xl bg-[#EAF7EE] px-3.5 py-2.5 text-[13px] text-[#1E7A34]">
          {t("emp.accessGranted")}
        </p>
      )}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-[26px] font-semibold tracking-tight sm:text-[30px]">Employees</h1>
        {!readOnly && (
        <button
          data-testid={TID.employees.add}
          onClick={startAdd}
          className="flex h-10 items-center gap-1.5 rounded-full bg-[#0071E3] px-4 text-[14px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98]"
        >
          <Plus className="h-4 w-4" /> Employee
        </button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B0B0B8]" />
        <input
          data-testid={TID.employees.search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search"
          className="focus-ring h-11 w-full rounded-full border border-[#E1E1E6] bg-white pl-10 pr-4 text-[15px] placeholder:text-[#B0B0B8]"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-24 text-[14px] text-[#8E8E93]">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : isMobile ? (
        <div className="space-y-3">
          {filtered.map((e) => (
            <div
              key={e.id}
              data-testid={TID.employees.row(e.id)}
              className={`card-soft px-4 py-3.5 ${e.aktif ? "" : "opacity-45"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 cursor-pointer items-center gap-3" data-testid={`emp-open-${e.id}`} onClick={() => navigate(`/employees/${e.id}`)}>
                  <EmployeeAvatar id={e.id} nama={e.nama} hasPhoto={!!(e.has_photo || e.photo_path)} size={40} />
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-[15px] font-semibold hover:text-[#0071E3]"><span className="truncate">{e.nama}</span><JaritBadge show={e.is_jarit} /></p>
                    {e.emp_code && <p className="num text-[12px] font-medium text-[#0071E3]">{e.emp_code}</p>}
                    <p className="num text-[12.5px] text-[#8E8E93]">
                      {e.is_jarit ? (e.jabatan || "Penjahit") : isAdmin ? `${e.jabatan || "—"} · Rate ${rp(e.rate_harian)}` : (e.jabatan || "—")}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {!readOnly && (<>
                  <button
                    data-testid={TID.employees.edit(e.id)}
                    onClick={() => startEdit(e)}
                    className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#F2F2F7] hover:text-[#1D1D1F]"
                    aria-label="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={TID.employees.del(e.id)}
                    onClick={() => setDelTarget(e)}
                    className="grid h-8 w-8 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-[#FDF0F2] hover:text-[#C8102E]"
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  </>)}
                </div>
              </div>
              {e.is_jarit ? (
                <div className="mt-2.5">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBEEF0] px-3 py-1 text-[12px] font-medium text-[#C8102E]">
                    <Scissors className="h-3.5 w-3.5" />
                    Ongkos Jarit — dikelola di Tukang Jarit
                  </span>
                  {e.no_hp && <span className="num ml-2 text-[12.5px] text-[#6E6E73]">{e.no_hp}</span>}
                </div>
              ) : (
                <>
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12.5px]">
                    {e.metode === "transfer" ? (
                      <span className="flex items-center gap-1.5">
                        <span className="font-semibold" style={{ color: bankColor(e.bank) }}>
                          {(e.bank || "").toUpperCase()}
                        </span>
                        <span className="num text-[#6E6E73]">
                          {rekParts(e.no_rekening).head}
                          <strong className="font-semibold text-[#1D1D1F]">
                            {rekParts(e.no_rekening).tail}
                          </strong>
                        </span>
                      </span>
                    ) : (
                      <Pill tone="red">Cash</Pill>
                    )}
                    {e.no_hp && <span className="num text-[#6E6E73]">{e.no_hp}</span>}
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <Pill tone={e.bpjs ? "navy" : "gray"}>BPJS {e.bpjs ? "Yes" : "No"}</Pill>
                    {isAdmin && (
                      <Pill tone={e.eligible_bonus ? "navy" : "gray"}>
                        Bonus {e.eligible_bonus ? "Auto" : "Excluded"}
                      </Pill>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="card-soft px-4 py-14 text-center text-[14px] text-[#8E8E93]">
              No results
            </p>
          )}
        </div>
      ) : (
        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead>
                <tr className="border-b border-black/[0.06]">
                  {[
                    { h: "Name", align: "text-left" },
                    { h: t("emp.position"), align: "text-left" },
                    ...(isAdmin ? [{ h: "Rate / Hari", align: "text-right" }] : []),
                    { h: "Payment", align: "text-left" },
                    { h: "Phone", align: "text-left" },
                    { h: "BPJS", align: "text-center" },
                    ...(isAdmin ? [{ h: "Bonus", align: "text-center" }] : []),
                    { h: "", align: "text-left" },
                  ].map((c, i) => (
                    <th
                      key={c.h + i}
                      className={`label whitespace-nowrap px-4 py-3 ${c.align}`}
                    >
                      {c.h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={TID.employees.row(e.id)}
                    className={`border-b border-black/[0.04] transition-colors last:border-0 hover:bg-[#FAFAFA] ${
                      e.aktif ? "" : "opacity-45"
                    }`}
                  >
                    <td className="px-4 py-3">
                      <button type="button" data-testid={`emp-open-${e.id}`} onClick={() => navigate(`/employees/${e.id}`)} className="flex items-center gap-2.5 text-left">
                        <EmployeeAvatar id={e.id} nama={e.nama} hasPhoto={!!(e.has_photo || e.photo_path)} size={32} />
                        <span>
                          <p className="flex items-center gap-1.5 text-[14.5px] font-medium transition-colors hover:text-[#0071E3]"><span className="truncate">{e.nama}</span><JaritBadge show={e.is_jarit} /></p>
                          {e.emp_code && <p className="num text-[11.5px] font-medium text-[#0071E3]">{e.emp_code}</p>}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-[13.5px] text-[#6E6E73]">
                      {e.jabatan || "\u2014"}
                    </td>
                    {e.is_jarit ? (
                      <td colSpan={isAdmin ? 5 : 3} className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBEEF0] px-3 py-1 text-[12.5px] font-medium text-[#C8102E]">
                          <Scissors className="h-3.5 w-3.5" />
                          Ongkos Jarit — gaji, BPJS &amp; kasbon dikelola di halaman Tukang Jarit
                        </span>
                      </td>
                    ) : (
                      <>
                        {isAdmin && (
                          <td className="num px-4 py-3 text-right text-[14px] font-medium">
                            {rp(e.rate_harian)}
                          </td>
                        )}
                        <td className="px-4 py-3">
                          {e.metode === "transfer" ? (
                            <span className="flex flex-wrap items-center gap-2">
                              <span
                                className="text-[13px] font-semibold"
                                style={{ color: bankColor(e.bank) }}
                              >
                                {(e.bank || "").toUpperCase()}
                              </span>
                              <span className="num text-[12.5px] text-[#6E6E73]">
                                {rekParts(e.no_rekening).head}
                                <strong className="font-semibold text-[#1D1D1F]">
                                  {rekParts(e.no_rekening).tail}
                                </strong>
                              </span>
                            </span>
                          ) : (
                            <Pill tone="red">Cash</Pill>
                          )}
                        </td>
                        <td className="num px-4 py-3 text-[13px] text-[#6E6E73]">
                          {e.no_hp || "\u2014"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Pill tone={e.bpjs ? "navy" : "gray"}>{e.bpjs ? "Yes" : "No"}</Pill>
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-center">
                            <Pill tone={e.eligible_bonus ? "navy" : "gray"}>
                              {e.eligible_bonus ? "Auto" : "Excluded"}
                            </Pill>
                          </td>
                        )}
                      </>
                    )}
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-1">
                        {!readOnly && (<>
                        <button
                          data-testid={TID.employees.edit(e.id)}
                          onClick={() => startEdit(e)}
                          className="grid h-8 w-8 place-items-center rounded-full text-[#8E8E93] transition-colors hover:bg-[#F2F2F7] hover:text-[#1D1D1F]"
                          aria-label="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={TID.employees.del(e.id)}
                          onClick={() => setDelTarget(e)}
                          className="grid h-8 w-8 place-items-center rounded-full text-[#B0B0B8] transition-colors hover:bg-[#FDF0F2] hover:text-[#C8102E]"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 8 : 6} className="px-4 py-16 text-center text-[14px] text-[#8E8E93]">
                      No results
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!delTarget}
        onOpenChange={(v) => !v && setDelTarget(null)}
        title="Delete this employee?"
        description={
          delTarget
            ? `${delTarget.nama}'s data will be permanently deleted. This action cannot be undone.`
            : ""
        }
        onConfirm={remove}
        testid={TID.employees.delDialog}
        confirmTestid={TID.employees.delConfirm}
      />
    </div>
  );
}
